import { ReactElement, useCallback, useMemo } from 'react';
import * as React from 'react';
import { FieldDataWithId, FormType } from '../types/form.types';
import useResponsiveLayout from '../styles/responsive-layout.hook';
import useReasons from './use-reasons.hook';
import ContactFieldsComponents from '../components/contact-form/contact-fields/contact-fields.components';

interface UseFieldLayoutReturnType {
  isWhiteCase: boolean;
  renderSpacing: (id: string) => ReactElement | null;
  customWidth: (id: string) => string | undefined;
  requiredLegendPlaceIndex: number | null;
}

const { Spacing } = ContactFieldsComponents;

const useFieldsLayout = (
  fieldsArray: FieldDataWithId[],
  type: FormType
): UseFieldLayoutReturnType => {
  const { isMobile } = useResponsiveLayout();
  const { mainReason } = useReasons();

  const isWhiteCase = useMemo(
    () => ['client_prospects', 'partner_prospects'].includes(type),
    [type]
  );

  const isWithSpacing = useCallback(
    (id: string) => type === 'client_prospects' || id === 'street',
    [type]
  );

  const addSpacingFieldsIds = useMemo(
    () =>
      type === 'partner_prospects'
        ? ['city', 'title', 'phone', 'street', 'product']
        : ['title', 'phone', 'post_code'],
    [type]
  );

  const renderSpacing = useCallback(
    (id: string): ReactElement | null => {
      if (isMobile || !addSpacingFieldsIds.includes(id)) return null;

      // return isWithSpacing(id) ? <Spacing addMargin /> : <Spacing />;
      return React.createElement(Spacing, { addMargin: isWithSpacing(id) });
    },
    [addSpacingFieldsIds, isMobile, isWithSpacing]
  );

  const requiredLegendPlaceIndex = useMemo(() => {
    if (mainReason) {
      return null;
    }
    if (isMobile) {
      return 0;
    }
    return fieldsArray.length && addSpacingFieldsIds.includes(fieldsArray[0].id)
      ? 0
      : 1;
  }, [addSpacingFieldsIds, fieldsArray, isMobile, mainReason]);

  const customWidth = useCallback(
    (id: string): string | undefined => {
      if (isMobile || !isWhiteCase) return undefined;

      switch (id) {
        case 'street':
          return '100%';
        case 'company_name':
        case 'city':
        case 'position':
          return '70%';
        case 'number_of_branches':
        case 'post_code':
        case 'title':
        case 'company_size':
          return '30%';
        default:
          return '50%';
      }
    },
    [isMobile, isWhiteCase]
  );

  return { isWhiteCase, renderSpacing, customWidth, requiredLegendPlaceIndex };
};

export default useFieldsLayout;
